import React from 'react';
import { Loader2 } from 'lucide-react';

type IconButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'success' | 'outline';
type IconButtonSize = 'sm' | 'md' | 'lg';

interface IconButtonProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  icon: React.ReactNode;
  variant?: IconButtonVariant;
  size?: IconButtonSize;
  loading?: boolean;
  'aria-label': string;
  className?: string;
}

const BASE =
  'inline-flex items-center justify-center rounded-lg transition-all cursor-pointer outline-none disabled:cursor-not-allowed';

const VARIANT_STYLES: Record<IconButtonVariant, { normal: string; disabled: string }> = {
  primary: {
    normal:
      'bg-gradient-to-r from-brand to-[#7c3aed] text-content-inverse hover:shadow-lg hover:shadow-brand/20',
    disabled: 'bg-surface-primary text-content-faint',
  },
  secondary: {
    normal:
      'border border-subtle text-content-secondary hover:text-content hover:bg-surface-primary hover:border-default',
    disabled: 'border border-subtle text-content-faint',
  },
  ghost: {
    normal: 'text-content-faint hover:text-content-secondary hover:bg-surface-primary',
    disabled: 'text-content-faint',
  },
  danger: {
    normal: 'text-content-faint hover:text-red-400 hover:bg-red-400/10',
    disabled: 'text-content-faint',
  },
  success: {
    normal:
      'bg-status-success/10 text-status-success border border-status-success/20 hover:bg-status-success/20',
    disabled: 'bg-surface-primary text-content-faint border border-subtle',
  },
  outline: {
    normal: 'border border-default text-content hover:bg-surface-primary',
    disabled: 'border border-subtle text-content-faint',
  },
};

const SIZE_STYLES: Record<IconButtonSize, string> = {
  sm: 'p-1.5',
  md: 'p-2',
  lg: 'p-2.5',
};

const FOCUS_RING = 'focus-visible:ring-2 focus-visible:ring-brand/50 focus-visible:ring-offset-1 focus-visible:ring-offset-surface-page';

const LOADER_SIZE: Record<IconButtonSize, number> = { sm: 14, md: 16, lg: 18 };

const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(
  (
    {
      icon,
      variant = 'ghost',
      size = 'md',
      loading = false,
      disabled = false,
      className = '',
      ...rest
    },
    ref,
  ) => {
    const isDisabled = disabled || loading;
    const vs = VARIANT_STYLES[variant];

    const classes = [
      BASE,
      SIZE_STYLES[size],
      FOCUS_RING,
      isDisabled ? vs.disabled : vs.normal,
      className,
    ].join(' ');

    return (
      <button
        ref={ref}
        className={classes}
        disabled={isDisabled}
        aria-busy={loading || undefined}
        {...rest}
      >
        {loading ? (
          <Loader2 size={LOADER_SIZE[size]} className="animate-spin" />
        ) : (
          icon
        )}
      </button>
    );
  },
);

IconButton.displayName = 'IconButton';

export default IconButton;
export type { IconButtonProps, IconButtonVariant, IconButtonSize };
